import React, { useEffect, useState } from "react";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import {
  Card,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import JumboSearch from "@jumbo/components/JumboSearch";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Avatar, Switch, FormControlLabel } from "@mui/material";
import {
  getDSOKPromotions,
  deleteDSOKPromotion,
} from "app/services/api/promotion";

const DSOKPromotions = () => {
  const { getAuthUser } = useJumboAuth();
  const {
    customer: { url },
  } = getAuthUser();

  const { t, i18n } = useTranslation();
  const { showDialog, hideDialog } = useJumboDialog();
  const navigate = useNavigate();
  const [searchName, setSearchName] = useState();

  const [promotions, setPromotions] = useState([]);
  const [defaultPromotions, setDefaultPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [customer, setCustomer] = useState();
  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || i18n.language;
  const languages = customer?.dsokSettings?.customer?.languages || [];

  const loadPromotions = async () => {
    setLoading(true);
    try {
      const items = await getDSOKPromotions();
      const query = (searchName || "").toLowerCase();
      const filtered = query
        ? items.filter((p) => {
            const localizedName =
              p?.name?.[defaultLanguage] ?? p?.name?.[i18n.language] ?? "";
            return String(localizedName).toLowerCase().includes(query);
          })
        : items;
      setPromotions(filtered || []);
      setDefaultPromotions(items || []);
    } catch (e) {
      setPromotions([]);
      setDefaultPromotions([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchName]);

  const handleDelete = async (promotion) => {
    hideDialog();
    setLoading(true);
    try {
      const result = await deleteDSOKPromotion(promotion.id);
      if (result) {
        loadPromotions();
      } else {
        enqueueSnackbar(
          t("notifications.wasFailed", {
            defaultValue: "Failed",
          }),
          { variant: "error" }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        t("notifications.wasFailed", {
          defaultValue: "Failed",
        }),
        { variant: "error" }
      );
    }
    setLoading(false);
  };

  const deleteConfirmation = (promotion) => {
    showDialog({
      variant: "confirm",
      title: t("widgets.confirmDialog.areYouSure"),
      onYes: () => handleDelete(promotion),
      onNo: () => hideDialog(),
    });
  };

  const handleEdit = (promotion) => {
    navigate(`/customer/dsok/promotions/${promotion.id}`);
  };

  return (
    <React.Fragment>
      <Typography variant={"h2"} mb={3}>
        {t("pages.dsokPromotions.title", { defaultValue: "Promotions" })}
      </Typography>
      <Stack
        direction="row"
        display="flex"
        alignItems="center"
        mb={2}
        spacing={1}
      >
        <Card sx={{ mb: 0, width: "50%" }}>
          <JumboSearch
            page="DSOKPromotions"
            onChange={setSearchName}
            sx={{ width: "100%" }}
            placeholder={t("pages.presentation.searchPresentation")}
          />
        </Card>
        <Tooltip
          title={t("pages.dsokPromotions.addPromotion", {
            defaultValue: "Add Promotion",
          })}
        >
          <IconButton onClick={() => navigate("/customer/dsok/promotions/new")}>
            <AddBoxIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {loading ? (
        <CircularProgress sx={{ m: "5px 50%" }} />
      ) : promotions?.length ? (
        promotions.map((promotion) => (
          <Card
            key={promotion.id}
            sx={{ p: 2, mb: 2, display: "flex", alignItems: "center" }}
          >
            <Typography variant="body2" sx={{ mr: 2 }}>
              {promotion.isActive
                ? t("pages.dsokPromotions.active", { defaultValue: "Active" })
                : t("pages.dsokPromotions.inactive", {
                    defaultValue: "Inactive",
                  })}
            </Typography>
            <Avatar
              src={promotion.image}
              variant="rounded"
              sx={{ width: 72, height: 72, mr: 2 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1">
                {promotion.name?.[defaultLanguage] ||
                  promotion.name?.[i18n.language] ||
                  ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {promotion.description?.[defaultLanguage] ||
                  promotion.description?.[i18n.language] ||
                  ""}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={t("buttons.edit")}>
                <IconButton onClick={() => handleEdit(promotion)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("buttons.delete")}>
                <IconButton onClick={() => deleteConfirmation(promotion)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Card>
        ))
      ) : (
        <JumboListNoDataPlaceHolder />
      )}
    </React.Fragment>
  );
};

export default DSOKPromotions;
